import { useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { RoutineCard } from "@/components/cards/routines/RoutineCard";
import { GoalCard } from "@/components/cards/goals/GoalCard";
import { useRoutines } from "@/hooks/routines/useRoutines";
import { useMobile } from "@/providers/mobileProvider";
import { useMobileNavbar } from "@/providers/mobileNavbarProvider";
import { Button } from "@/components/ui/button";
import { HEALTH_AREA_MAPPING } from "@/seed/HealthAreaMapping";
import { cn } from "@/lib/utils";
import { useGoals } from "@/hooks/goals/useGoals";
import type { Goal, HealthArea, Routine } from "instantdb/types";
import { PATHS } from "@/config/paths";
import { seo } from "@/utilities/seo";
import { NotFound } from "@/not-found";
import { GlobalError } from "@/global-error";
import { Container } from "@/components/ui/container";
import { useTranslation } from "@/hooks/useTranslation";
import { Heading } from "@/components/ui/heading";

export const Route = createFileRoute("/dashboard/library/health-area/$slug/")({
  component: LibraryCategoryPage,
  notFoundComponent: NotFound,
  errorComponent: GlobalError,
  head: ({ params }) => {
    const category = HEALTH_AREA_MAPPING[params.slug as keyof typeof HEALTH_AREA_MAPPING];
    if (!category) return {};

    // Use fallback English titles for SEO since we can't access translation here
    const fallbackTitles: Record<string, string> = {
      nutrition: "Nutrition",
      energy: "Energy",
      fitness: "Fitness",
      heart: "Heart",
      hydration: "Hydration",
      mental: "Mental",
      mood: "Mood",
      movement: "Movement",
      sleep: "Sleep",
      stress: "Stress",
      weight: "Weight",
    };

    const title = fallbackTitles[params.slug] || "Health Area";

    return {
      meta: [
        {
          charSet: "utf-8",
        },
        {
          name: "viewport",
          content: "width=device-width, initial-scale=1, viewport-fit=cover",
        },
        {
          title: `${title} - ProgressMade.ai`,
        },
        ...seo({
          title: `${title} - ProgressMade.ai`,
          description: `Browse ${title.toLowerCase()} goals and routines in the ProgressMade.ai library.`,
        }),
      ],
    };
  },
});

function LibraryCategoryPage() {
  const { slug: categorySlug } = Route.useParams();
  const router = useRouter();
  const { isMobile } = useMobile();
  const { setMobileNavbar } = useMobileNavbar();
  const { goals: userGoals } = useGoals();
  const { routines: userRoutines } = useRoutines();
  const { t } = useTranslation();

  const hasUsedGoal = (goalId: string) => {
    return userGoals?.some((goal) => goal.originalGoal?.goalId === goalId);
  };

  const hasUsedRoutine = (routineId: string) => {
    return userRoutines?.some((routine) => routine.originalRoutine?.routineId === routineId);
  };

  const category = Object.values(HEALTH_AREA_MAPPING).find(
    (category) => category.slug === categorySlug,
  ) as HealthArea;

  useEffect(() => {
    if (isMobile && category) {
      setMobileNavbar({
        title: t(category.title),
        showBackButton: true,
      });
    }
  }, [isMobile, category, setMobileNavbar]);

  if (!category) {
    return <div>Category not found</div>;
  }

  // Get routines and goals directly from the category object
  const routines = (category.routines as Routine[]) || [];
  const goals = (category.goals as Goal[]) || [];

  return (
    <Container>
      {!isMobile && (
        <div className="col-span-12 flex items-center gap-4">
          <Button
            variant="outline"
            size="icon"
            className="rounded-full shadow-sm hover:shadow-md transition-all duration-200 border-gray-100/80 dark:border-gray-800/80"
            onClick={() => router.navigate({ to: PATHS.LIBRARY })}
          >
            <ArrowLeft className="size-4" />
          </Button>
        </div>
      )}

      <div className={cn("col-span-12 rounded-xl p-6", category.color)}>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-6">
            <div
              className={cn(
                "rounded-full p-3 text-xl min-w-13 min-h-13 flex items-center justify-center",
                category.color,
              )}
            >
              {category.symbol}
            </div>
            <div className="flex flex-col gap-3">
              <div>
                <Heading level="h1" size="h5" className="font-semibold text-foreground">
                  {t(category.title)}
                </Heading>
                <p className="text-muted-foreground max-w-2xl">{t(category.description)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {routines.length > 0 && (
        <>
          <div className="col-span-12 space-y-4">
            <Heading level="h2" size="h5" className="font-semibold">
              Routines
            </Heading>
            <div className="space-y-4">
              {routines.map((routine) => (
                <RoutineCard
                  key={routine.id}
                  routine={routine}
                  isLibrary
                  isCopied={routine.id ? hasUsedRoutine(routine.id) : false}
                />
              ))}
            </div>
          </div>
        </>
      )}

      {goals.length > 0 && (
        <>
          <div className="col-span-12 space-y-4">
            <Heading level="h2" size="h5" className="font-semibold">
              Goals
            </Heading>
            <div className="space-y-4">
              {goals.map((goal) => (
                <GoalCard
                  key={goal.id}
                  goal={goal}
                  disableClickThrough
                  isLibrary
                  isCopied={goal.id ? hasUsedGoal(goal.id) : false}
                />
              ))}
            </div>
          </div>
        </>
      )}

      {goals.length === 0 && routines.length === 0 && (
        <div className="col-span-12">
          <div className="rounded-lg border border-dashed p-8 text-center">
            <p className="text-muted-foreground">{t("library.no_routines_or_goals_available")}</p>
          </div>
        </div>
      )}
    </Container>
  );
}
